'use client'

import {useQuery} from 'react-query'
import {getRequest} from '../../../setup/redux/common/cruds'
import type {AxiosResponse} from 'axios'
import type {PackagesQueryResponse} from '../../modules/packages/packages-list/core/_models'
import {Col, Row, Table} from 'react-bootstrap'
import React, {useEffect, useState} from 'react'
import {KTCard, KTCardBody} from '../../../_metronic/helpers'
import {GetPNRNumber} from '../../modules/bookings/helper'
import moment from 'moment'
import Pagination from '@mui/material/Pagination'
import {UpdateConfirmationNumber} from '../../modules/bookings/pages/transferPage/helpers/UpdateConfirmationNumber'
import {Link} from 'react-router-dom'
import {useCheckAccess} from '../../services/Functional'
import {OnTourStatusSelect} from './partial/OnTourStatusSelect'
import {ManageCustomHotelSupplier} from './partial/ManageCustomHotelSupplier'
import {formatPhone} from '../../utils/formatPhone'
import {useDispatch} from 'react-redux'
import {getCurrencyDropdown} from '../../../setup/redux/suppliers/actions'
import {
  HotelStatusCounts,
  parseHotelStatusCounts,
  HOTEL_BOOKING_STATUS_OPTIONS,
  SUPPLIER_PAYMENT_STATUS_OPTIONS,
  HOTEL_DATE_FILTER_OPTIONS,
  HotelDateFilter,
} from './partial/statusCounts'

const HOTELS_DEFAULT_DATE = moment().add(1, 'day').format('YYYY-MM-DD')

export const ManageBookingHotels = (props: {
  country: string
  countryCode: string
  onStatusCounts?: (counts: HotelStatusCounts | undefined) => void
}) => {
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [dateFilterType, setDateFilterType] = useState<HotelDateFilter>('created_at')

  // Pending filter state (bound to inputs, applied on Search click)
  const [searchName, setSearchName] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [serviceDate, setServiceDate] = useState(HOTELS_DEFAULT_DATE)
  const [isSearchApplied, setIsSearchApplied] = useState(false)

  // Applied filter state (drives the query)
  const [appliedSearchName, setAppliedSearchName] = useState('')
  const [appliedDateFrom, setAppliedDateFrom] = useState('')
  const [appliedDateTo, setAppliedDateTo] = useState('')
  const [appliedServiceDate, setAppliedServiceDate] = useState('')

  const [bookingStatus, setBookingStatus] = useState('')
  const [supplierPaymentStatus, setSupplierPaymentStatus] = useState('')
  const [appliedBookingStatus, setAppliedBookingStatus] = useState('')
  const [appliedSupplierPaymentStatus, setAppliedSupplierPaymentStatus] = useState('')

  const checkAccess = useCheckAccess()
  const scope = checkAccess('dashboard.show_all') ? 'all' : 'own'

  const activeDate = appliedServiceDate || HOTELS_DEFAULT_DATE
  const buildUrl = () => {
    let url = `${process.env.REACT_APP_API_BASE_URL}dashboard/hotels/${activeDate}?page=${currentPage}&items_per_page=10&country=${props.countryCode}&date_filter=${dateFilterType}&scope=${scope}`
    if (appliedSearchName) url += `&search_name=${encodeURIComponent(appliedSearchName)}`
    if (appliedDateFrom) url += `&date_from=${appliedDateFrom}`
    if (appliedDateTo) url += `&date_to=${appliedDateTo}`
    if (appliedBookingStatus) url += `&booking_status=${appliedBookingStatus}`
    if (appliedSupplierPaymentStatus)
      url += `&supplier_payment_status=${appliedSupplierPaymentStatus}`
    return url
  }

  const {
    isFetching,
    refetch,
    data: response,
  } = useQuery(
    `DASHBOARD-MANAGE-HOTELS-${props.countryCode}-${activeDate}-${dateFilterType}-${appliedSearchName}-${appliedDateFrom}-${appliedDateTo}-${appliedBookingStatus}-${appliedSupplierPaymentStatus}-${currentPage}`,
    () => getRequest(buildUrl()).then((d: AxiosResponse<PackagesQueryResponse>) => d.data),
    {
      cacheTime: 5000,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: false,
      refetchInterval: 60000,
    }
  )
  const handlePaginationChange = async (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page)
  }
  const handleSearch = () => {
    setAppliedSearchName(searchName)
    setAppliedDateFrom(dateFrom)
    setAppliedDateTo(dateTo)
    setAppliedServiceDate(serviceDate)
    setAppliedBookingStatus(bookingStatus)
    setAppliedSupplierPaymentStatus(supplierPaymentStatus)
    setIsSearchApplied(true)
    setCurrentPage(1)
  }
  const handleClear = () => {
    setSearchName('')
    setDateFrom('')
    setDateTo('')
    setServiceDate(HOTELS_DEFAULT_DATE)
    setAppliedSearchName('')
    setAppliedDateFrom('')
    setAppliedDateTo('')
    setAppliedServiceDate(HOTELS_DEFAULT_DATE)
    setIsSearchApplied(false)
    setDateFilterType('created_at')
    setBookingStatus('')
    setSupplierPaymentStatus('')
    setAppliedBookingStatus('')
    setAppliedSupplierPaymentStatus('')
    setCurrentPage(1)
  }

  useEffect(() => {
    refetch()
  }, [currentPage])

  // Loaded once here rather than in each OnTour row, which would fire the same request
  // once per service on screen.
  const dispatch = useDispatch()
  useEffect(() => {
    dispatch(getCurrencyDropdown())
  }, [])

  // The filters live here but the badges render in the accordion header, which the wrapper owns.
  const onStatusCounts = props.onStatusCounts
  useEffect(() => {
    onStatusCounts?.(parseHotelStatusCounts(response?.data?.status_counts))
  }, [response, onStatusCounts])

  const [showUpdateConfirmationModal, setShowUpdateConfirmationModal] = useState(false)
  const [confirmationService, setConfirmationService] = useState<{
    id: number
    name: string
    confirmation_no: string
    service: string
    hotel_net_amount?: number
    hotel_net_currency?: string
    hotel_due_date?: string
  } | null>(null)

  const [showSupplierModal, setShowSupplierModal] = useState(false)
  const [activeCustomHotel, setActiveCustomHotel] = useState<any>(null)

  // Helper function to get status badge class
  const getStatusBadgeClass = (status: string) => {
    if (!status) return 'secondary'
    switch (status.toLowerCase()) {
      case 'confirmed':
        return 'success'
      case 'pending':
        return 'warning'
      case 'cancelled':
        return 'danger'
      default:
        return 'secondary'
    }
  }

  return (
    <>
      <Row className='align-items-end mb-4 g-2'>
        <Col md={3}>
          <label className='form-label fw-semibold small text-muted mb-1'>Search Name</label>
          <input
            className='form-control'
            type='text'
            placeholder='Search name...'
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
        </Col>
        <Col md={2}>
          <label className='form-label fw-semibold small text-muted mb-1'>Date Range From</label>
          <input
            className='form-control'
            type='date'
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </Col>
        <Col md={2}>
          <label className='form-label fw-semibold small text-muted mb-1'>Date Range To</label>
          <input
            className='form-control'
            type='date'
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </Col>
        <Col md={2}>
          <label className='form-label fw-semibold small text-muted mb-1'>Booking Status</label>
          <select
            className='form-select'
            value={bookingStatus}
            onChange={(e) => setBookingStatus(e.target.value)}
          >
            {HOTEL_BOOKING_STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Col>
        <Col md={2}>
          <label className='form-label fw-semibold small text-muted mb-1'>
            Supplier Payment Status
          </label>
          <select
            className='form-select'
            value={supplierPaymentStatus}
            onChange={(e) => setSupplierPaymentStatus(e.target.value)}
          >
            {SUPPLIER_PAYMENT_STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Col>
        <Col md={2} className='border-start ps-3'>
          <label className='form-label fw-semibold small text-muted mb-1'>Service Date</label>
          <input
            className='form-control'
            type='date'
            value={serviceDate}
            onChange={(e) => {
              setServiceDate(e.target.value)
              setAppliedServiceDate(e.target.value)
            }}
            disabled={isSearchApplied}
          />
        </Col>
        <Col md='auto' className='d-flex gap-2 align-items-end'>
          <div className='d-flex flex-column me-2'>
            <label className='form-label fw-semibold small text-muted mb-1'>Date Filter By</label>
            <div className='d-flex gap-3 pt-1'>
              {HOTEL_DATE_FILTER_OPTIONS.map((option) => (
                <div className='form-check' key={option.value}>
                  <input
                    className='form-check-input'
                    type='radio'
                    name={`dateFilterType-${props.countryCode}`}
                    id={`filter-${option.value}-${props.countryCode}`}
                    checked={dateFilterType === option.value}
                    onChange={() => setDateFilterType(option.value)}
                  />
                  <label
                    className='form-check-label text-nowrap'
                    htmlFor={`filter-${option.value}-${props.countryCode}`}
                  >
                    {option.label}
                  </label>
                </div>
              ))}
            </div>
          </div>
          <button className='btn btn-primary' onClick={handleSearch}>
            Search
          </button>
          <button className='btn btn-secondary' onClick={handleClear}>
            Clear
          </button>
        </Col>
      </Row>
      <KTCard className='gutter-b shadow-sm'>
        <KTCardBody className='p-0'>
          <Row>
            {response?.data?.hotels?.map((booking: any, index: number) => (
              <React.Fragment key={`booking_${index}`}>
                {/* Render Regular Hotels */}
                {booking?.booking_hotels?.map((hotelItem: any, hotelIndex: number) => (
                  <Col
                    md={12}
                    key={`hotel_${index}_${hotelIndex}_${hotelItem.id}`}
                    className='mb-4'
                  >
                    <Table
                      bordered
                      responsive
                      className='vertical-align-middle table-striped font-weight-bold custom-table-border custom-table-padding'
                    >
                      <thead>
                        <tr className='vertical-align-middle'>
                          <th className='text-capitalize gap-2 align-items-center'>
                            <span className='badge badge-warning me-2'>Hotel</span>
                            <span className='fw-bold'>{hotelItem.hotel?.name}</span>
                            <span
                              className={`badge badge-${getStatusBadgeClass(
                                hotelItem.booking_status
                              )} ms-2`}
                            >
                              {hotelItem.booking_status}
                            </span>
                          </th>
                          <th className='text-center'>
                            <i className='fa fa-calendar me-1'></i>
                            Check-in: {moment(hotelItem.check_in).format('DD/MM/YYYY')}
                          </th>
                          <th className='text-center'>
                            <i className='fa fa-calendar me-1'></i>
                            Check-out: {moment(hotelItem.check_out).format('DD/MM/YYYY')}
                          </th>
                          <th>
                            <div className='d-flex justify-content-between vertical-align-middle'>
                              <Link
                                target='_blank'
                                to={`/bookings/${booking.identifier}/show`}
                                className='btn btn-sm btn-outline-primary'
                              >
                                <i className='fa fa-external-link me-1'></i>
                                {GetPNRNumber({booking: booking})}
                              </Link>
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className='text-capitalize w-50'>
                            <i className='fa fa-user me-1 text-primary'></i>
                            {`${booking.lead_passenger.prefix}. ${booking.lead_passenger.first_name} ${booking.lead_passenger.last_name}`}
                          </td>
                          <td colSpan={2}>
                            <span className='badge badge-info me-2'>
                              <i className='fa fa-moon me-1'></i>
                              {hotelItem.total_night} Night{hotelItem.total_night > 1 ? 's' : ''}
                            </span>
                            <span className='badge badge-primary'>
                              <i className='fa fa-bed me-1'></i>
                              {hotelItem.total_rooms} Room{hotelItem.total_rooms > 1 ? 's' : ''}
                            </span>
                          </td>
                          <td>
                            {hotelItem.non_refundable && (
                              <span className='badge badge-danger'>
                                <i className='fa fa-ban me-1'></i>
                                Non-Refundable
                              </span>
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={2}>
                            <div className='mb-2'>
                              <span className='fw-bold me-1'>Location:</span>
                              <i className='fa fa-map-marker me-1 text-danger'></i>
                              {hotelItem.hotel?.city?.name}, {hotelItem.hotel?.country?.name}
                            </div>
                          </td>
                          <td colSpan={2}>
                            {hotelItem.cancel_upto && (
                              <div>
                                <span className='fw-bold me-1'>Cancel Upto:</span>
                                <i className='fa fa-calendar me-1 text-warning'></i>
                                {moment(hotelItem.cancel_upto).format('DD/MM/YYYY')}
                              </div>
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={4}>
                            <div className='fw-bold mb-2'>Rooms:</div>
                            {hotelItem.rooms?.map((room: any, roomIndex: number) => (
                              <div
                                key={`room_${roomIndex}`}
                                className='d-flex align-items-center mb-2 p-2 bg-light rounded'
                              >
                                <div className='flex-grow-1'>
                                  <div className='fw-bold'>{room.room?.name}</div>
                                  <div className='small'>
                                    <span className='badge badge-primary me-1'>
                                      <i className='fa fa-user me-1'></i>
                                      {room.adults?.length} Adult
                                      {room.adults?.length > 1 ? 's' : ''}
                                    </span>
                                    {room.children?.length > 0 && (
                                      <span className='badge badge-info'>
                                        <i className='fa fa-child me-1'></i>
                                        {room.children?.length} Child
                                        {room.children?.length > 1 ? 'ren' : ''}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={4}>
                            <div className='mb-2'>
                              <span className='fw-bold me-1'>Special Request:</span>
                              <span className='ms-2'>
                                {hotelItem.special_request || 'No special request'}
                              </span>
                            </div>
                            {hotelItem.rsvn_remark && (
                              <div>
                                <span className='fw-bold me-1'>Reservation Remark:</span>
                                <span className='ms-2'>{hotelItem.rsvn_remark}</span>
                              </div>
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={4}>
                            <div className='d-flex align-items-center'>
                              <span className='badge badge-light text-dark p-2 border'>
                                <i className='fa fa-check-circle me-1 text-success'></i>
                                Confirmation No: {hotelItem.confirmation_no || 'Not confirmed'}
                              </span>
                              {hotelItem.confirmation_date && (
                                <span className='ms-2 text-muted'>
                                  Confirmed on:{' '}
                                  {moment(hotelItem.confirmation_date).format('DD/MM/YYYY HH:mm')}
                                </span>
                              )}
                              <button
                                className='btn btn-sm btn-light ms-2'
                                onClick={() => {
                                  setConfirmationService({
                                    id: hotelItem.id,
                                    name: hotelItem.hotel?.name || 'Hotel',
                                    confirmation_no: hotelItem.confirmation_no || '',
                                    service: 'hotel',
                                    hotel_net_amount: hotelItem.hotel_net_amount,
                                    hotel_net_currency: hotelItem.hotel_net_currency,
                                    hotel_due_date: hotelItem.hotel_due_date,
                                  })
                                  setShowUpdateConfirmationModal(true)
                                }}
                                title='Edit Confirmation Number'
                              >
                                <i className='fa fa-edit text-primary'></i>
                              </button>
                            </div>
                            <div className='mt-2'>
                              <OnTourStatusSelect
                                service='hotel'
                                id={hotelItem.id}
                                status={hotelItem.ontour_status}
                                chargeAmount={hotelItem.ontour_charge_amount}
                                chargeCurrency={hotelItem.ontour_charge_currency}
                                defaultCurrency={hotelItem.hotel_net_currency}
                                onSaved={refetch}
                              />
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </Col>
                ))}

                {/* Render Custom Hotels */}
                {booking?.booking_custom_hotel_services?.map(
                  (customHotelItem: any, customHotelIndex: number) => (
                    <Col
                      md={12}
                      key={`custom_hotel_${index}_${customHotelIndex}_${customHotelItem.id}`}
                      className='mb-4'
                    >
                      <Table
                        bordered
                        responsive
                        className='vertical-align-middle table-striped font-weight-bold custom-table-border custom-table-padding'
                      >
                        <thead>
                          <tr className='vertical-align-middle'>
                            <th className='text-capitalize gap-2 align-items-center'>
                              <span className='badge badge-dark me-2'>Custom Hotel</span>
                              <span className='fw-bold'>{customHotelItem.name}</span>
                              <span
                                className={`badge badge-${getStatusBadgeClass(
                                  customHotelItem.booking_status
                                )} ms-2`}
                              >
                                {customHotelItem.booking_status}
                              </span>
                            </th>
                            <th className='text-center'>
                              <i className='fa fa-calendar me-1'></i>
                              Check-in: {moment(customHotelItem.check_in).format('DD/MM/YYYY')}
                            </th>
                            <th className='text-center'>
                              <i className='fa fa-calendar me-1'></i>
                              Check-out: {moment(customHotelItem.check_out).format('DD/MM/YYYY')}
                            </th>
                            <th>
                              <div className='d-flex justify-content-between vertical-align-middle'>
                                <Link
                                  target='_blank'
                                  to={`/bookings/${booking.identifier}/show`}
                                  className='btn btn-sm btn-outline-primary'
                                >
                                  <i className='fa fa-external-link me-1'></i>
                                  {GetPNRNumber({booking: booking})}
                                </Link>
                              </div>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className='text-capitalize w-50'>
                              <i className='fa fa-user me-1 text-primary'></i>
                              {`${booking.lead_passenger.prefix}. ${booking.lead_passenger.first_name} ${booking.lead_passenger.last_name}`}
                            </td>
                            <td colSpan={2}>
                              <span className='badge badge-info me-2'>
                                <i className='fa fa-moon me-1'></i>
                                {customHotelItem.total_night} Night
                                {customHotelItem.total_night > 1 ? 's' : ''}
                              </span>
                              <span className='badge badge-primary'>
                                <i className='fa fa-bed me-1'></i>
                                {customHotelItem.total_rooms} Room
                                {customHotelItem.total_rooms > 1 ? 's' : ''}
                              </span>
                            </td>
                            <td>
                              {customHotelItem.currency && (
                                <span className='badge badge-secondary'>
                                  <i className='fa fa-money me-1'></i>
                                  {customHotelItem.currency.code}
                                </span>
                              )}
                            </td>
                          </tr>
                          <tr>
                            <td colSpan={2}>
                              <div className='mb-2'>
                                <span className='fw-bold me-1'>Location:</span>
                                <i className='fa fa-map-marker me-1 text-danger'></i>
                                {customHotelItem.city?.name}, {customHotelItem.country?.name}
                              </div>
                              {customHotelItem.address && (
                                <div className='small text-muted'>
                                  <i className='fa fa-location-arrow me-1'></i>
                                  {customHotelItem.address}
                                </div>
                              )}
                            </td>
                            <td colSpan={2}>
                              {customHotelItem.cancel_upto && (
                                <div>
                                  <span className='fw-bold me-1'>Cancel Upto:</span>
                                  <i className='fa fa-calendar me-1 text-warning'></i>
                                  {moment(customHotelItem.cancel_upto).format('DD/MM/YYYY')}
                                </div>
                              )}
                            </td>
                          </tr>
                          <tr>
                            <td colSpan={4}>
                              <div className='fw-bold mb-2'>Rooms:</div>
                              {customHotelItem.rooms?.map((room: any, roomIndex: number) => (
                                <div
                                  key={`room_${roomIndex}`}
                                  className='d-flex align-items-center mb-2 p-2 bg-light rounded'
                                >
                                  <div className='flex-grow-1'>
                                    <div className='fw-bold'>
                                      {room.room_type || 'Room ' + (roomIndex + 1)}
                                    </div>
                                    <div className='small'>
                                      <span className='badge badge-primary me-1'>
                                        <i className='fa fa-user me-1'></i>
                                        {room.adults?.length} Adult
                                        {room.adults?.length > 1 ? 's' : ''}
                                      </span>
                                      {room.children?.length > 0 && (
                                        <span className='badge badge-info'>
                                          <i className='fa fa-child me-1'></i>
                                          {room.children?.length} Child
                                          {room.children?.length > 1 ? 'ren' : ''}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </td>
                          </tr>
                          <tr>
                            <td colSpan={4}>
                              <div className='mb-2'>
                                <span className='fw-bold me-1'>Special Request:</span>
                                <span className='ms-2'>
                                  {customHotelItem.special_request || 'No special request'}
                                </span>
                              </div>
                              {customHotelItem.rsvn_remark && (
                                <div>
                                  <span className='fw-bold me-1'>Reservation Remark:</span>
                                  <span className='ms-2'>{customHotelItem.rsvn_remark}</span>
                                </div>
                              )}
                            </td>
                          </tr>
                          {checkAccess('bookings.view_financials') && (
                            <tr>
                              <td colSpan={4}>
                                <button
                                  className='btn btn-link p-0 text-decoration-none mb-2'
                                  onClick={() => {
                                    setActiveCustomHotel(customHotelItem)
                                    setShowSupplierModal(true)
                                  }}
                                >
                                  <i
                                    className={`fa ${
                                      customHotelItem.supplier ? 'fa-edit' : 'fa-plus-circle'
                                    } me-1`}
                                  ></i>{' '}
                                  Supplier:
                                </button>
                                {customHotelItem.supplier ? (
                                  <div className='d-flex align-items-center mb-2 p-2 bg-light rounded'>
                                    <div className='flex-grow-1'>
                                      <div className='fw-bold'>
                                        {customHotelItem.supplier.company_name}
                                      </div>
                                      <div className='small'>
                                        <i className='fa fa-user me-1'></i>{' '}
                                        {customHotelItem.supplier.contact_person}
                                        <i className='fa fa-phone ms-2 me-1'></i>{' '}
                                        {formatPhone(customHotelItem.supplier.phone)}
                                      </div>
                                      {customHotelItem.purchase_price_original > 0 && (
                                        <div className='small text-muted mt-1'>
                                          <i className='fa fa-money-bill me-1'></i>
                                          {customHotelItem.currency?.name}{' '}
                                          {Number(customHotelItem.purchase_price_original).toFixed(
                                            2
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <span className='ms-2 text-muted'>No supplier assigned</span>
                                )}
                              </td>
                            </tr>
                          )}
                          <tr>
                            <td colSpan={2}>
                              <div className='d-flex align-items-center'>
                                <span className='badge badge-light text-dark p-2 border'>
                                  <i className='fa fa-check-circle me-1 text-success'></i>
                                  Confirmation No:{' '}
                                  {customHotelItem.confirmation_no || 'Not confirmed'}
                                </span>
                                {customHotelItem.confirmation_date && (
                                  <span className='ms-2 text-muted'>
                                    Confirmed on:{' '}
                                    {moment(customHotelItem.confirmation_date).format(
                                      'DD/MM/YYYY HH:mm'
                                    )}
                                  </span>
                                )}
                                <button
                                  className='btn btn-sm btn-light ms-2'
                                  onClick={() => {
                                    setConfirmationService({
                                      id: customHotelItem.id,
                                      name: customHotelItem.name || 'Custom Hotel',
                                      confirmation_no: customHotelItem.confirmation_no || '',
                                      service: 'custom_hotel',
                                      hotel_net_amount: customHotelItem.hotel_net_amount,
                                      hotel_net_currency: customHotelItem.hotel_net_currency,
                                      hotel_due_date: customHotelItem.hotel_due_date,
                                    })
                                    setShowUpdateConfirmationModal(true)
                                  }}
                                  title='Edit Confirmation Number'
                                >
                                  <i className='fa fa-edit text-primary'></i>
                                </button>
                              </div>
                              <div className='mt-2'>
                                <OnTourStatusSelect
                                  service='custom_hotel'
                                  id={customHotelItem.id}
                                  status={customHotelItem.ontour_status}
                                  chargeAmount={customHotelItem.ontour_charge_amount}
                                  chargeCurrency={customHotelItem.ontour_charge_currency}
                                  defaultCurrency={customHotelItem.hotel_net_currency}
                                  onSaved={refetch}
                                />
                              </div>
                            </td>
                            <td colSpan={2}>
                              {customHotelItem.sell_price > 0 && (
                                <div>
                                  <span className='fw-bold me-1'>Sell Price: $</span>
                                  <span className='badge badge-success'>
                                    {customHotelItem.currency?.code}{' '}
                                    {customHotelItem.sell_price.toFixed(2)}
                                  </span>
                                </div>
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </Col>
                  )
                )}
              </React.Fragment>
            ))}
          </Row>
          <Row>
            <Col>
              <div className='d-flex justify-content-center mt-4'>
                <div className='p-2 rounded shadow-sm'>
                  <Pagination
                    count={Number(response?.data?.count ?? 1)}
                    page={currentPage}
                    siblingCount={2}
                    onChange={handlePaginationChange}
                    shape='rounded'
                    color='primary'
                  />
                </div>
              </div>
            </Col>
          </Row>
        </KTCardBody>
      </KTCard>
      {showUpdateConfirmationModal && confirmationService && (
        <UpdateConfirmationNumber
          show={showUpdateConfirmationModal}
          hide={() => {
            setShowUpdateConfirmationModal(false)
            setConfirmationService(null)
            refetch()
          }}
          id={confirmationService.id}
          name={confirmationService.name}
          confirmation_no={confirmationService.confirmation_no}
          service={confirmationService.service}
          hotel_net_amount={confirmationService.hotel_net_amount}
          hotel_net_currency={confirmationService.hotel_net_currency}
          hotel_due_date={confirmationService.hotel_due_date}
        />
      )}
      {showSupplierModal && activeCustomHotel && (
        <ManageCustomHotelSupplier
          show={showSupplierModal}
          hide={() => setShowSupplierModal(false)}
          customHotel={activeCustomHotel}
          refetch={refetch}
        />
      )}
    </>
  )
}
